import { localStorageService } from '@shared/services';
import { TFunctionKeys } from 'i18next';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation as useTranslationI18n } from 'react-i18next';
import {
  ADDITIONAL_LANGS,
  DEFAULT_COUNTRY,
  DEFAULT_LANGUAGE,
  SUPPORTED_COUNTRIES,
  SUPPORTED_LANGS,
  SUPPORTED_LANGS_PER_COUNTRY,
} from '@shared/consts';
import * as Sentry from '@sentry/react';
import { Spinner } from '@shared/components';
import { initTranslations } from '@shared/translations';
import { TranslationLabels } from '@generated/translation-labels';
import { Context } from './context';

const translationKeysEnv = process.env.REACT_APP_SHOW_TRANSLATION_KEYS;

const CIMODE_LANGUAGE: GenericTypes.Language = 'cimode';

export const Provider: FC = ({ children }) => {
  const {
    t: i18nT,
    i18n,
    ready: translationReady,
  } = useTranslationI18n(SUPPORTED_COUNTRIES, { useSuspense: false });
  // useCountry() hook cannot be used here
  // because TranslationsProvider cannot be nested inside AuthProvider
  const [userCountry, setUserCountry] = useState<GenericTypes.Country>(
    localStorageService.getCountryCode() || DEFAULT_COUNTRY,
  );

  const langCode = useMemo(() => {
    if (!translationReady) return DEFAULT_LANGUAGE;

    return (ADDITIONAL_LANGS.includes(
      i18n.languages[0] as GenericTypes.AdditionalLanguage,
    )
      ? i18n.languages[1]
      : i18n.languages[0]) as GenericTypes.Language;
  }, [i18n.languages, translationReady]);

  const supportedLanguages = useMemo(() => {
    const languages = userCountry
      ? SUPPORTED_LANGS_PER_COUNTRY[userCountry]
      : SUPPORTED_LANGS;

    return translationKeysEnv ? [...languages, CIMODE_LANGUAGE] : languages;
  }, [userCountry]);

  const changeLanguage = useCallback(
    async (language: GenericTypes.Language) => {
      if (!translationReady) return;

      await i18n.changeLanguage(language);
    },
    [i18n, translationReady],
  );

  const setLocalization = useCallback(async () => {
    if (!translationReady || !userCountry) return;

    Sentry.setTag('country', userCountry);

    if (!SUPPORTED_LANGS_PER_COUNTRY[userCountry]?.includes(langCode)) {
      await changeLanguage(DEFAULT_LANGUAGE);
    }
  }, [changeLanguage, userCountry, langCode, translationReady]);

  useEffect(() => {
    setLocalization().catch(Sentry.captureException);
    // setLocalization() should be called only after translationCountry change
    // user's country affects supported languages list (only when user is logged in)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCountry]);

  useEffect(() => {
    initTranslations().catch(Sentry.captureException);
  }, []);

  const t = useCallback(
    (key: TFunctionKeys, options?) => {
      return i18nT(key, { ...options, ns: userCountry });
    },
    [i18nT, userCountry],
  );

  useEffect(() => {
    if (translationReady) {
      document.title = t(TranslationLabels.pageTitle);
    }
  }, [t, translationReady]);

  return translationReady ? (
    <Context.Provider
      value={{
        langCode,
        supportedLanguages,
        t,
        changeLanguage,
        setUserCountry,
      }}
    >
      {children}
    </Context.Provider>
  ) : (
    <Spinner />
  );
};

Provider.displayName = 'TranslationsProvider';
